import React, { useState, useMemo } from 'react';
import { X, Copy, Check, Award } from 'lucide-react';

interface BadgeModalProps {
  score: number;
  riskLevel: string;
  repoFullName: string;
  onClose: () => void;
}

function buildBadge(score: number, riskLevel: string, repoFullName: string) {
  const shieldColor =
    score >= 80 ? 'brightgreen' :
    score >= 60 ? 'yellow' :
    score >= 40 ? 'orange' : 'red';

  const shieldLabel =
    score >= 80 ? 'secure' :
    score >= 60 ? 'medium_risk' :
    score >= 40 ? 'high_risk' : 'critical';

  // shield.io: dashes separate sections, underscores become spaces
  const shieldUrl =
    `https://img.shields.io/badge/GitHubMate-${score}%25_${shieldLabel}-${shieldColor}?style=flat-square`;

  const scanUrl =
    `https://githubmate.ai/analyze?repo=${encodeURIComponent(repoFullName)}`;

  const markdown =
    `[![GitHubMate Security Score](${shieldUrl})](${scanUrl})`;

  const html =
    `<a href="${scanUrl}"><img src="${shieldUrl}" alt="GitHubMate Security Score" /></a>`;

  return { shieldUrl, scanUrl, markdown, html };
}

type CopyTarget = 'markdown' | 'html' | null;

const BadgeModal: React.FC<BadgeModalProps> = ({ score, riskLevel, repoFullName, onClose }) => {
  const [copied, setCopied] = useState<CopyTarget>(null);

  const { shieldUrl, markdown, html } = useMemo(
    () => buildBadge(score, riskLevel, repoFullName),
    [score, riskLevel, repoFullName]
  );

  const copy = (target: CopyTarget, text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(target);
    setTimeout(() => setCopied(null), 2200);
  };

  const scoreColor =
    score >= 80 ? '#4E9630' :
    score >= 60 ? '#BC821D' :
    score >= 40 ? '#E07B3F' : '#C0392B';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-charcoal-card border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-accent-teal" />
            <span className="font-semibold text-gray-100 text-sm">README Security Badge</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors p-1 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">

          {/* Live preview */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Preview</p>
            <div className="bg-gray-900 rounded-lg p-4 flex items-center gap-3">
              <img
                src={shieldUrl}
                alt="GitHubMate badge preview"
                className="h-5"
                style={{ imageRendering: 'auto' }}
              />
              <span className="text-xs text-gray-500">← live render from shield.io</span>
            </div>
          </div>

          {/* Score context */}
          <div className="flex items-center gap-3 rounded-lg px-4 py-3 border"
            style={{ borderColor: `${scoreColor}40`, background: `${scoreColor}0d` }}>
            <span className="text-2xl font-bold" style={{ color: scoreColor }}>{score}%</span>
            <div>
              <p className="text-xs font-semibold" style={{ color: scoreColor }}>
                {riskLevel.toUpperCase()} RISK · {repoFullName}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Score is from this scan. Re-scan anytime to refresh.</p>
            </div>
          </div>

          {/* Markdown */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Paste into <code className="text-accent-teal">README.md</code>
            </p>
            <pre className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
{markdown}
            </pre>
            <button
              onClick={() => copy('markdown', markdown)}
              className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                copied === 'markdown'
                  ? 'bg-lime/10 border-lime/40 text-lime'
                  : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-400'
              }`}
            >
              {copied === 'markdown'
                ? <><Check className="h-3 w-3" /> Copied!</>
                : <><Copy className="h-3 w-3" /> Copy Markdown</>
              }
            </button>
          </div>

          {/* HTML */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Or use <code className="text-accent-teal">HTML</code>
            </p>
            <pre className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
{html}
            </pre>
            <button
              onClick={() => copy('html', html)}
              className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                copied === 'html'
                  ? 'bg-lime/10 border-lime/40 text-lime'
                  : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-400'
              }`}
            >
              {copied === 'html'
                ? <><Check className="h-3 w-3" /> Copied!</>
                : <><Copy className="h-3 w-3" /> Copy HTML</>
              }
            </button>
          </div>

          {/* Note */}
          <p className="text-xs text-gray-600 border-t border-gray-700 pt-4">
            The badge score is static — it reflects this scan.
            Clicking the badge rescans the repo on GitHubMate so anyone can get the latest results.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BadgeModal;
